import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#3B82F6", "#22C55E", "#EAB308", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#F97316"];
const STATUS_LABELS = { new: "New", contacted: "Contacted", tour_scheduled: "Tour Scheduled", tour_complete: "Tour Complete", contract_sent: "Contract Sent", contract_signed: "Signed", paid: "Paid", cancelled: "Cancelled", refunded: "Refunded", lost: "Lost" };

export default function ReportsPage() {
  const [bySource, setBySource] = useState([]);
  const [byStatus, setByStatus] = useState([]);
  const [byType, setByType] = useState([]);
  const [conversion, setConversion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/reports/leads-by-source").then(r => setBySource(r.data.data)),
      api.get("/reports/leads-by-status").then(r => setByStatus(r.data.data)),
      api.get("/reports/leads-by-type").then(r => setByType(r.data.data)),
      api.get("/reports/conversion").then(r => setConversion(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-72 w-full" />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reports-page">
      <h1 className="font-outfit text-2xl font-bold tracking-tight">Reports</h1>

      {/* Conversion KPI */}
      {conversion && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-border"><CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Leads</p>
            <p className="text-2xl font-mono font-bold mt-1">{conversion.total}</p>
          </CardContent></Card>
          <Card className="border-border"><CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Paid</p>
            <p className="text-2xl font-mono font-bold mt-1 text-green-500">{conversion.paid}</p>
          </CardContent></Card>
          <Card className="border-border"><CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Signed</p>
            <p className="text-2xl font-mono font-bold mt-1 text-emerald-500">{conversion.signed}</p>
          </CardContent></Card>
          <Card className="border-border"><CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Conversion Rate</p>
            <p className="text-2xl font-mono font-bold mt-1 text-blue-500">{conversion.rate}%</p>
          </CardContent></Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads by Source */}
        <Card className="border-border">
          <CardHeader><CardTitle className="text-base font-outfit">Leads by Source</CardTitle></CardHeader>
          <CardContent>
            {bySource.length === 0 ? <p className="text-muted-foreground text-sm text-center py-8">No data</p> : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={bySource} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={100} label={({ source, count }) => `${source}: ${count}`}>
                    {bySource.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Leads by Status */}
        <Card className="border-border">
          <CardHeader><CardTitle className="text-base font-outfit">Leads by Status</CardTitle></CardHeader>
          <CardContent>
            {byStatus.length === 0 ? <p className="text-muted-foreground text-sm text-center py-8">No data</p> : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={byStatus.map(d => ({ ...d, label: STATUS_LABELS[d.status] || d.status }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Leads by Type */}
        <Card className="border-border">
          <CardHeader><CardTitle className="text-base font-outfit">Leads by Inquiry Type</CardTitle></CardHeader>
          <CardContent>
            {byType.length === 0 ? <p className="text-muted-foreground text-sm text-center py-8">No data</p> : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={byType.map(d => ({ ...d, label: d.type?.replace("_", " ") }))} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={100} label={({ label, count }) => `${label}: ${count}`}>
                    {byType.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
